(() => {
  if (customElements.get('x1-scroll-story')) return;
  const clamp = (value) => Math.max(0, Math.min(1, value));
  const smooth = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
  // Keep the rotation pace; remove the long static tails from the two internal views.
  const boundaries = [0, 2.6 / 7, 5.4 / 7, 1];
  const clipEnds = [Infinity, 7, 4];
  class X1ScrollStory extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.scenes = [...this.querySelectorAll('[data-x1-scene]')];
      this.videos = this.scenes.map((scene) => scene.querySelector('video'));
      this.stage = this.querySelector('.x1-scroll-story__stage');
      this.runway = this.querySelector('.x1-scroll-story__runway');
      this.staticQuery = matchMedia('(prefers-reduced-motion: reduce), (max-height: 639px)');
      this.near = false;
      this.targets = [0, 0, 0];
      this.queue = () => {
        if (!this.frame && this.near && this.hasAttribute('data-enhanced')) {
          this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
        }
      };
      this.videos.forEach((video, index) => {
        video.muted = true;
        video.addEventListener('loadeddata', this.queue, options);
        video.addEventListener('seeked', () => {
          video.dataset.ready = '';
          this.seek(index);
        }, options);
        video.addEventListener('error', () => {
          video.removeAttribute('data-ready');
          video.dataset.failed = '';
        }, options);
      });
      window.addEventListener('scroll', this.queue, { ...options, passive: true });
      window.addEventListener('resize', this.queue, options);
      this.staticQuery.addEventListener('change', () => this.configure(), options);
      this.observer = new IntersectionObserver((entries) => {
        this.near = entries.some((entry) => entry.isIntersecting);
        this.queue();
      }, { rootMargin: '400px 0px' });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        // Edited copy or browser zoom must never be clipped inside a sticky viewport.
        const style = getComputedStyle(this.stage);
        const available = this.stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        const overflowing = this.scenes.some((scene) => scene.scrollHeight > available + 1);
        if (this.hasAttribute('data-enhanced') && overflowing) this.configure(true);
        this.queue();
      });
      this.resizeObserver.observe(this.stage);
      this.configure();
    }
    configure(forceStatic = false) {
      const enabled = !forceStatic && !this.staticQuery.matches && !navigator.connection?.saveData;
      this.toggleAttribute('data-enhanced', enabled);
      this.scenes.forEach((scene, index) => {
        scene.toggleAttribute('data-active', index === 0);
        scene.removeAttribute('data-visible');
        scene.style.removeProperty('--x1-opacity');
        scene.style.removeProperty('--x1-rise');
        scene.removeAttribute('aria-hidden');
        scene.inert = false;
      });
      this.videos.forEach((video) => {
        video.pause();
        if (!enabled) {
          video.removeAttribute('src');
          video.removeAttribute('data-ready');
          video.load();
        }
      });
      this.queue();
    }
    load(index) {
      const video = this.videos[index];
      if (!video || video.hasAttribute('src') || video.dataset.failed !== undefined) return;
      video.preload = 'auto';
      video.src = video.dataset.src;
      video.load();
    }
    seek(index) {
      const video = this.videos[index];
      if (!this.isConnected || !this.hasAttribute('data-enhanced') || video.readyState < 2 || video.seeking || video.dataset.failed !== undefined) return;
      const fps = Number(video.dataset.fps);
      const target = Math.min(Math.max(0, video.duration - 1 / fps), Math.round(this.targets[index] * fps) / fps);
      if (Math.abs(video.currentTime - target) > .5 / fps) {
        try { video.currentTime = target; } catch { /* Poster remains available if seeking is unsupported. */ }
      } else {
        video.dataset.ready = '';
      }
    }
    render() {
      // A queued scroll frame may run after a viewport/motion change disabled animation.
      if (!this.hasAttribute('data-enhanced') || !this.near) return;
      const top = parseFloat(getComputedStyle(this.stage).top) || 0;
      const distance = Math.max(1, this.runway.offsetHeight - this.stage.offsetHeight);
      const total = clamp((top - this.runway.getBoundingClientRect().top) / distance);
      const index = total < boundaries[1] ? 0 : total < boundaries[2] ? 1 : 2;
      const local = clamp((total - boundaries[index]) / (boundaries[index + 1] - boundaries[index]));
      // A short, reversible overlap keeps the handoff tied to the scroll position.
      const overlap = .018;
      this.scenes.forEach((scene, i) => {
        const active = i === index;
        const enter = i === 0 ? 1 : smooth((total - boundaries[i] + overlap) / (overlap * 2));
        const leave = i === 2 ? 1 : 1 - smooth((total - boundaries[i + 1] + overlap) / (overlap * 2));
        const opacity = enter * leave;
        scene.toggleAttribute('data-visible', opacity > 0);
        scene.style.setProperty('--x1-opacity', opacity);
        scene.style.setProperty('--x1-rise', `${(1 - enter) * 16 - (1 - leave) * 12}px`);
        if (!active && scene.contains(document.activeElement)) this.focus({ preventScroll: true });
        scene.toggleAttribute('data-active', active);
        scene.setAttribute('aria-hidden', String(!active));
        scene.inert = !active;
        if (opacity > 0) {
          this.load(i);
          const video = this.videos[i];
          const phase = clamp((total - boundaries[i]) / (boundaries[i + 1] - boundaries[i]));
          const duration = Number.isFinite(video.duration) ? Math.min(video.duration, clipEnds[i]) : 0;
          // Only the rotating introduction holds its last frame; final views lead straight onward.
          this.targets[i] = clamp((phase - .08) / (i === 0 ? .84 : .92)) * duration;
          this.seek(i);
        }
      });
      // Defer each next clip until the current chapter is well underway.
      if (local > .55) this.load(index + 1);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.videos?.forEach((video) => { video.pause(); video.removeAttribute('src'); video.load(); });
    }
  }
  customElements.define('x1-scroll-story', X1ScrollStory);
})();
